import {
  LeftCircleOutlined,
  LoadingOutlined,
  PlusCircleOutlined,
  StopOutlined,
  CloseCircleOutlined
} from "@ant-design/icons";
import { Button, Col, Descriptions, Form, Input, Modal, Row, Select, notification } from "antd";
import {
  useCancelComplaintMutation,
  useCloseComplaintMutation,
  useCreateCustomerConnectLogForComplaintMutation,
  useLazyGetComplaintByIdQuery,
  useGetCustomerConnectLogsForComplaintQuery,
  useOnHoldComplaintMutation,
  useUpdateCustomerConnectLogForComplaintMutation
} from "../../../redux/services/complaintApi";
import { useAppSelector } from "../../../redux/hooks";
import ConnectLogTabel from "../ConnectLogTabel";
import { Link, useNavigate, useParams } from "react-router-dom";
import ConnectLogForm from "../ConnectLogForm";
import { useState, useEffect } from "react";
import ErrorDetails from "../../../components/ErrorDetails";
import { localizeDateTime, localizeDate, customerAddress, getStatusText } from "../../../utils";
import {
  holdOptions,
  cancelComplaintOptions,
  closeComplaintOptions
} from "../../../constants/helperConstants";


const ViewComplaint = (props: any) => {
  const navigate = useNavigate();

  const params = useParams();
  const [form] = Form.useForm();

  const [api, contextHolder] = notification.useNotification();

  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setEditMode] = useState(false);
  const [selectedCallLogData, setSelectedCallLogData] = useState({});

  const [getComplaintById, {
    data,
    isLoading,
    refetch: refetchComplaintById
  }] = useLazyGetComplaintByIdQuery();
  const complaint = data?.complaint;

  const { data: customerConnectLogs, refetch: refetchCustomerConnectLogs } =
    useGetCustomerConnectLogsForComplaintQuery(params.complaintId);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showFullView, setShowFullView] = useState(false);
  const [modalType, setModalType] = useState("");

  const [cancelComplaint] =
    useCancelComplaintMutation();

  const [onHoldComplaint] =
    useOnHoldComplaintMutation();

  const [closeComplaint] =
    useCloseComplaintMutation();

  const [onCreateCustomerConnectLogForComplaint] =
    useCreateCustomerConnectLogForComplaintMutation();
  const [onUpdateCustomerConnectLogForComplaint] =
    useUpdateCustomerConnectLogForComplaintMutation();

  const user = useAppSelector<any>((store) => store.auth.user);

  const isClient = user?.user_type === "client";

  useEffect(() => {
    if (params?.complaintId) {
      getComplaintById(params.complaintId);
    }
  }, [params?.complaintId]);

  const handleCancelOrHoldComplaint = (type: string) => {
    setModalType(type);
    setIsModalOpen(true);
  };

  const onFinish = async (values: any) => {
    if (modalType === "HOLD") {
      try {
        await onHoldComplaint({ id: params.complaintId, reason: values.reason }).unwrap();
        api.success({
          message: "Complaint marked as on hold operation successful",
          placement: "top"
        });
        form.resetFields();
        setIsModalOpen(false);
        refetchComplaintById();
      } catch (error: any) {
        api.info({
          message: "Complaint marked as on hold operation failed",
          description: <ErrorDetails errors={error?.data || []} />,
          placement: "top"
        });
      }
    }

    if (modalType === "CANCEL") {
      try {
        await cancelComplaint({ id: params.complaintId, reason: values.cancel_reason }).unwrap();
        api.success({
          message: "Complaint cancelled successfully",
          placement: "top"
        });
        form.resetFields();
        refetchComplaintById();
        setIsModalOpen(false);
      } catch (error: any) {
        api.info({
          message: "Complaint cancelled operation failed",
          description: <ErrorDetails errors={error?.data || []} />,
          placement: "top"
        });
      }
    }

    if (modalType === "CLOSE") {
      try {
        await closeComplaint({
          id: params.complaintId,
          close_reason: values.close_reason,
          close_complaint_summary: values.close_complaint_summary
        }).unwrap();
        api.success({
          message: "Complaint closed successfully",
          placement: "top"
        });
        form.resetFields();
        refetchComplaintById();
        setIsModalOpen(false);
      } catch (error: any) {
        api.info({
          message: "Complaint closed operation failed",
          description: <ErrorDetails errors={error?.data || []} />,
          placement: "top"
        });
      }
    }
  };

  const onAddCallLog = async (values: object, setLoading: any, form: any) => {
    try {
      setLoading(true);
      await onCreateCustomerConnectLogForComplaint(values).unwrap();
      api.success({
        message: "Call log created for complaint",
        placement: "top"
      });
      form.resetFields();
      refetchCustomerConnectLogs();
      setLoading(false);
      setShowModal(false);
    } catch (error: any) {
      api.info({
        message: "Create call log for complaint operation failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const onUpdateCallLog = async (values: object, setLoading: any, form: any) => {
    try {
      setLoading(true);
      await onUpdateCustomerConnectLogForComplaint(values).unwrap();
      api.success({
        message: "Call log updated for complaint",
        placement: "top"
      });
      form.resetFields();
      refetchCustomerConnectLogs();
      setLoading(false);
      setShowModal(false);
    } catch (error: any) {
      api.info({
        message: "Update call log for complaint operation failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const onUpdateCallLogModal = (callLog: any) => {
    setSelectedCallLogData(callLog);
    setEditMode(true);
    setShowModal(true);
  };

  const onUpdate = (callLog: any) => {
    setSelectedCallLogData(callLog);
    setEditMode(true);
    setShowModal(true);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  const handleCancel = () => {
    setModalType("");
    setIsModalOpen(false);
  };

  const onGoBack = () => {
    navigate(-1);
  };

  return (
    <>
      {contextHolder}
      {!isLoading && data && (
        <div style={{ paddingBottom: 50 }}>
          <Row style={{ marginBottom: 20 }}>
            <Col
              sm={24}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <h2>Complaint Details</h2>
              <Button onClick={onGoBack} size="middle" type="default" icon={<LeftCircleOutlined />}>
                Back
              </Button>
            </Col>
          </Row>
          {!isClient && (
            <Row style={{ marginBottom: 20 }}>
              <Col
                sm={24}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <div></div>
                <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                  <Link to={"create-visit"}>
                    <Button icon={<PlusCircleOutlined />}>Create Visit</Button>
                  </Link>
                  <Button
                    icon={<StopOutlined />}
                    onClick={() => handleCancelOrHoldComplaint("CANCEL")}
                  >
                    Cancel
                  </Button>
                  <Button
                    icon={<CloseCircleOutlined />}
                    onClick={() => handleCancelOrHoldComplaint("CLOSE")}
                  >
                    Close Complaint
                  </Button>
                  {/* <Button icon={<PauseCircleOutlined />} onClick={() => handleCancelOrHold("HOLD")}>
                  Hold
                </Button> */}
                  <Button icon={<PlusCircleOutlined />} onClick={() => setShowModal(true)}>
                    Create Complaint Log
                  </Button>
                </div>
              </Col>
            </Row>
          )}

          <Descriptions
            title=""
            bordered
            column={1}
            labelStyle={{ fontWeight: 700, width: 250 }}
            size="middle"
          >
            <Descriptions.Item label="eCB Complaint Code">
              {data?.complaint?.ecb_complaint_code || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Installation Code">
              {data?.complaint?.ecb_install_code || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Survey Code">
              {data?.complaint?.ecb_survey_code || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Charger Type">
              {data?.complaint?.obc || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Installation Date">
              {data?.complaint?.actual_install_date_time
                ? localizeDate(data?.complaint?.actual_install_date_time)
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Complaint Receive Date">
              {data?.complaint?.complaint_receive_date
                ? localizeDate(data?.complaint?.complaint_receive_date)
                : "-"}
            </Descriptions.Item>
          </Descriptions>
          <br />

          <h3>Customer Details</h3>
          <Descriptions
            title=""
            bordered
            column={1}
            labelStyle={{ fontWeight: 700, width: 250 }}
            size="middle"
          >
            <Descriptions.Item label="Name">
              {data?.complaint?.customer_name || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Phone">
              {data?.complaint?.contact_number || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Address">
              {customerAddress(data?.complaint)}
            </Descriptions.Item>
            <Descriptions.Item label="EV City">
              {data?.complaint?.city?.name || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Survey Confirmed">
              {data?.complaint?.survey_done_same_day ? "YES" : "NO"}
            </Descriptions.Item>
          </Descriptions>

          <h3>Vehicle Details</h3>
          <Descriptions
            title=""
            bordered
            column={1}
            labelStyle={{ fontWeight: 700, width: 250 }}
            size="middle"
          >
            <Descriptions.Item label="Dealer">
              {data?.complaint?.dealer_name || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Dealer Loc.">
              {data?.complaint?.dealer_location || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Brand Model">
              {data?.complaint?.brand_model?.name || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Brand Complaint Number">
              {data?.complaint?.brand_complaint_number || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Model Name">
              {data?.complaint?.complaint_brand_model_name || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Model Color">
              {data?.complaint?.model_colour || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="VIN Number">{data?.complaint?.vin || "-"}</Descriptions.Item>
            <Descriptions.Item label="Installer Partner">
              {data?.complaint?.install_partner?.name || "-"}
            </Descriptions.Item>
          </Descriptions>

          <h3>Timelines</h3>
          <Descriptions
            title=""
            bordered
            column={1}
            labelStyle={{ fontWeight: 700, width: 250 }}
            size="middle"
          >
            <Descriptions.Item label="Status">{getStatusText(data?.complaint)}</Descriptions.Item>
            <br />
            <Descriptions.Item label="Scheduled Date - Time">
              {data?.complaint?.scheduled_install_date_time
                ? localizeDateTime(data?.complaint?.scheduled_install_date_time)
                : "-"}
            </Descriptions.Item>
            <br />
            <Descriptions.Item label="First Contact">
              {data?.complaint?.first_contact_at
                ? localizeDateTime(data?.complaint?.first_contact_at)
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {data?.complaint?.created_at ? localizeDateTime(data?.complaint?.created_at) : "-"}
            </Descriptions.Item>
            <br />
            <Descriptions.Item label="Last Updated">
              {data?.complaint?.updated_at ? localizeDateTime(data?.complaint?.updated_at) : "-"}
            </Descriptions.Item>
          </Descriptions>

          {!showFullView && (
            <Button type="link" onClick={() => setShowFullView(true)} style={{ marginTop: "20px" }}>
              Show More
            </Button>
          )}

          {showFullView && (
            <>
              <br />
              <Descriptions
                title=""
                bordered
                column={1}
                labelStyle={{ fontWeight: 700, width: 250 }}
                size="middle"
              >
                <Descriptions.Item label="Brand Code">
                  {data?.complaint?.brand?.code || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Cust. Alt. Contact">
                  {data?.complaint?.alt_contact_number || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Cust. Email">
                  {data?.complaint?.email || "-"}
                </Descriptions.Item>
                <br />
                <Descriptions.Item label="Record - Cust. Ok?">
                  {data?.complaint?.customer_ok_with_recording ? "YES" : "NO"}
                </Descriptions.Item>
                <br />
                <Descriptions.Item label="Model Desc.">
                  {data?.complaint?.model_description || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Variant">
                  {data?.complaint?.model_variant || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Int Color">
                  {data?.complaint?.model_int_color || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Ext Color">
                  {data?.complaint?.model_ext_color || "-"}
                </Descriptions.Item>

                <br />

                <Descriptions.Item label="Latitude">
                  {data?.complaint?.latitude || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Longitude">
                  {data?.complaint?.longitude || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Outstation">
                  {data?.complaint?.is_outstation ? "YES" : "NO"}
                </Descriptions.Item>

                <Descriptions.Item label="Site POC Name">
                  {data?.complaint?.site_poc_name || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Site POC Contact">
                  {data?.complaint?.site_poc_contact || "-"}
                </Descriptions.Item>
                <br />
                <Descriptions.Item label="Billed to Client">
                  {data?.complaint?.customer_billing || "-"}
                </Descriptions.Item>
              </Descriptions>
            </>
          )}

          {showFullView && (
            <Button
              type="link"
              onClick={() => setShowFullView(false)}
              style={{ marginTop: "20px" }}
            >
              Show Less
            </Button>
          )}

          <ConnectLogTabel
            title="Complaint customer connect logs"
            data={customerConnectLogs?.customer_connect_logs || []}
            msil={data?.complaint?.brand?.send_reports_to_api}
            onUpdate={onUpdate}
            onUpdateCallLogModal={onUpdateCallLogModal}
          />
        </div>
      )}

      {isLoading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh"
          }}
        >
          <LoadingOutlined style={{ fontSize: 24 }} spin />
        </div>
      )}
      <>
        <Modal
          title={`${modalType === "CLOSE"
            ? "Close Complaint"
            : modalType === "HOLD"
              ? "Put Complaint on Hold"
              : "Cancel Complaint"
            }`}
          open={isModalOpen}
          onCancel={handleCancel}
          footer={[
            <Button key="cancel" onClick={handleCancel}>
              Cancel
            </Button>,
            <Button form="myForm" key="submit" htmlType="submit" type="primary">
              Submit
            </Button>
          ]}
        >
          <Form
            name="myForm"
            form={form}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            style={{ maxWidth: 600 }}
            initialValues={{ remember: true }}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            autoComplete="off"
          >
            <Form.Item
              label={
                modalType === "CLOSE"
                  ? "Reason for close"
                  : modalType === "HOLD"
                    ? "Reason for putting on hold"
                    : "Reason for cancel"
              }
              name={
                modalType === "CLOSE"
                  ? "close_reason"
                  : modalType === "HOLD"
                    ? "on_hold_reason"
                    : "cancel_reason"
              }
              rules={[{ required: true, message: "Please input reason!" }]}
            >
              <Select
                placeholder="Select status"
                showSearch
                optionFilterProp="children"
                filterOption={(inputValue, option: any) =>
                  option.label.toLowerCase().includes(inputValue.toLowerCase())
                }
                options={
                  modalType === "CLOSE"
                    ? closeComplaintOptions
                    : modalType === "HOLD"
                      ? holdOptions
                      : cancelComplaintOptions
                }
              ></Select>
            </Form.Item>
            {modalType === "CLOSE" && (
              <Form.Item
                name="close_complaint_summary"
                label="Summary"
                rules={[{ required: true, message: "Please input summary!" }]}
              >
                <Input />
              </Form.Item>
            )}
          </Form>
        </Modal>

        {showModal && (
          <ConnectLogForm
            assignment={complaint}
            title={isEditMode ? "Update customer connect logs" : "Create customer connect logs"}
            isOpen={showModal}
            msil={data?.complaint?.brand?.send_reports_to_api}
            nextFollowUpRequired={data?.complaint?.brand?.next_follow_up_required}
            handleCancel={() => {
              setShowModal(false);
              setEditMode(false);
            }}
            isEditMode={isEditMode}
            onAddCallLog={!isEditMode && onAddCallLog}
            onUpdateCallLog={isEditMode && onUpdateCallLog}
            selectedCallLogData={isEditMode && selectedCallLogData}
            booking
          />
        )}
      </>
    </>
  );
};

export default ViewComplaint;
